import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ReactComponent as CloseIcon } from '@/assets/icons/close.icon.svg';
import Input from '@/components/Input';
import Select from 'react-select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assignLenders, createLenderTerms, fetchLenders, fetchOptions } from '@/api/lendersApi';
import Button from '@/components/Button';
import toast from 'react-hot-toast';
import { Oval } from 'react-loader-spinner';

const schema = yup.object({
  lenders: yup
    .array()
    .min(1, 'Please select at least one lender')
    .required('Please select a lender')
});

export const AssignLendersModal = ({ showModal, setShowModal, selectedTerm }) => {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  const handleAssignLenders = useMutation({
    mutationFn: (data) => assignLenders(data),
    onSuccess: () => {
      queryClient.invalidateQueries('lenders');
      queryClient.invalidateQueries('lendersTerms');
      queryClient.invalidateQueries('assignedLenders');
      toast.success('Lenders assigned successfully');
      setShowModal(false);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    }
  })

  const lenders = useQuery({
    queryKey: ['lenders', { page: 1, limit: 100 }],
    queryFn: fetchOptions
  });

  const lender = lenders?.data?.data?.users;

  const lenderOptions =
    lender?.map((item) => ({
      value: item?.lenderId,
      label: item?.company
    })) || [];

  const onSubmit = (data) => {
    const formData = {
      lenderId: Array.isArray(data.lenders) 
      ? data.lenders.map((item) => item.value) 
      : [],
      id: selectedTerm?.id
    }
    // console.log(formData);
    handleAssignLenders.mutate(formData);
  };

  return (
    <AnimatePresence>
      {showModal && (
        <CreateTermsView
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}>
          <motion.div
            initial={{ y: 1000 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            transition={{ duration: 0.3 }}
            className="modal"
            onClick={(e) => e.stopPropagation()}>
            <Content>
              <div className="header">
                <CloseIcon
                  onClick={() => {
                    reset();
                    setShowModal(false);
                  }}
                />
              </div>

              <FormView onSubmit={handleSubmit(onSubmit)}>
                <Title>Assign Lender's To this Term</Title>

                {/* Multi-Select Dropdown */}
                <Section>
                  <Label>Select a lender</Label>
                  <Controller
                    name="lenders"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={lenderOptions}
                        isMulti
                        isSearchable
                        placeholder="Search Lender"
                        menuShouldScrollIntoView={false}
                        menuPlacement="auto"
                        styles={{
                          control: (base) => ({
                            ...base,
                            height: '60px',
                            borderColor: '#EFF0F7',
                            borderRadius: '5px',
                            border: '1px solid #EFF0F7',
                            backgroundColor: '#EFF0F7',
                            padding: '4px'
                          }),
                          menu: (base) => ({
                            ...base,
                            maxHeight: '200px',
                            overflowY: 'auto'
                          }),
                          menuList: (base) => ({
                            ...base,
                            maxHeight: '200px',
                            overflowY: 'auto'
                          }),
                          option: (base, { isFocused, isSelected }) => ({
                            ...base,
                            backgroundColor: isFocused
                              ? '#EFF0F7'
                              : isSelected
                                ? '#d3e0ea'
                                : 'white',
                            color: isFocused || isSelected ? '#000' : '#333',
                            cursor: 'pointer'
                          })
                        }}
                      />
                    )}
                  />
                  <Error>{errors.lenders?.message}</Error>
                </Section>

                <div className="btn">
                  <Button className="approve" type="submit">
                    {handleAssignLenders.isLoading ? (
                      <Oval height={30} width={30} color="#fff" secondaryColor="#ddd" />
                    ) : (
                      'Assign Lender'
                    )}
                  </Button>
                </div>
              </FormView>
            </Content>
          </motion.div>
        </CreateTermsView>
      )}
    </AnimatePresence>
  );
};

const CreateTermsView = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;

  .modal {
    background-color: ${(props) => props.theme.colors?.white};
    padding: 20px;
    border-radius: 8px;
    min-width: 40vw;
    /* min-height: calc(100vh - 100px); */
  }
`;

const FormView = styled.form`
  width: 500px;
  margin-top: 30px;
  margin-bottom: 50px;

  .btn {
    margin-top: 40px;
    
    .approve {
      width: 40%;
      border-radius: 18px;
      color: ${({ theme }) => theme.colors.white};
      background-color: ${({ theme }) => theme.colors.primary};
    }
  }

  /* display: flex; */
  /* flex-direction: column;
  align-items: center;
  justify-content: center; */
`;

const Content = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .header {
    display: flex;
    justify-content: end;
    align-items: center;
    width: 100%;

    h2 {
      font-size: 1.5rem;
      font-weight: 500;
    }

    svg {
      cursor: pointer;
    }
  }
`;

const Title = styled.h2`
  font-weight: 600;
  font-size: 1.5rem;
  margin-bottom: 2.5rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const Section = styled.div`
width: 100%;
  margin-bottom: 2rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 1rem;
`;

const Options = styled.div`
  display: flex;
  gap: 1rem;
`;

const Option = styled.div`
  /* width: 100%; */
  display: flex;
  align-items: center;
`;

const OptionLabel = styled.label`
  /* width: 100%; */
  margin-left: 0.5rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const Radio = styled.input`
  /* width: 20%; */
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: #2ecc71;
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.3s;

  &:hover {
    background: #27ae60;
  }
`;

const Error = styled.p`
  font-size: 0.875rem;
  color: #e74c3c;
  margin-top: 0.25rem;
`;